import type { ModalNavOnChangeHandler } from '@core/components'
import { ModalBody, ModalFooter, ModalHeader, ModalNav, ModalRight } from '@core/components'
import {
  calculateSurfaceByKrigingAction,
  calculateSurfaceByMAAction,
  calculateSurfaceByMCAction,
  calculateSurfaceByRBFAction,
  calculateSurfaceBySCAction,
  selectSurfaceCalcState,
  useAppDispatch,
  useAppSelector,
} from '@core/core/store'
import type { BaseEntity } from '@core/core/types'
import { isStringNullOrEmpty } from '@core/core/utils'
import { IconGeoMap } from '@core/icons'
import { usingMovingModal } from '@library/model'
import { nanoid } from '@reduxjs/toolkit'
import cn from 'classnames'
import type { Nullable } from 'common/types'
import { forwardRef, useCallback, useEffect, useState } from 'react'

import {
  krigingDefaultField,
  minimalCurvatureDefaultField,
  movingAverageDefaultField,
  mpGroups,
  sequentialConvergenceDefaultField,
} from './constants'
import { Footer } from './Footer'
import { getFullNavItems, isValidGeometry, isValidMethodParams } from './helpers'
import { useMapBuilding } from './hooks'
import css from './MapBuilding.css'
import { ModalBuildingRightMenu } from './ModalBuildingRightMenu'
import type {
  AddedSelectItemType,
  ChoiceItemsType,
  MapBuilderItem,
  MapBuilderItemType,
  MapBuildingProps,
  MethodItemsType,
} from './types'
import { choiceItems, methodItems } from './types'

const MapBuilding = forwardRef<HTMLDivElement, MapBuildingProps>(function MapBuilding(
  { closeModal, projectId, onClose },
  ref,
) {
  const [activeStepNew, setActiveStepNew] = useState<MapBuilderItemType>('object')

  const handleOnMenuChange = useCallback<ModalNavOnChangeHandler<MapBuilderItem>>(item => {
    setActiveStepNew(item.id)
  }, [])

  const [methodValue, setMethodValue] = useState<MethodItemsType>(methodItems[1])
  const [choiceValue, setChoiceValue] = useState<ChoiceItemsType>(choiceItems[0])
  const [faultsFolderValue, setFaultsFolderValue] = useState<Nullable<BaseEntity>>(null)
  const [addedSelectItemList, setAddedSelectItemList] = useState<AddedSelectItemType[]>([
    { id: nanoid(), value: null, color: '' },
  ])
  const dispatch = useAppDispatch()
  const surfaceCalcState = useAppSelector(selectSurfaceCalcState)
  const isCalculating = surfaceCalcState === 'calculating'
  const {
    formData,
    updateSourcesData,
    updateGeometryData,
    updateTargetData,
    updateKrigingData,
    updateKernelData,
    updateFaultsData,
    updateSequentialConvergenceData,
    updateMinimalCurvatureData,
    updateMovingAverageData,
  } = useMapBuilding(projectId)

  useEffect(() => {
    switch (methodValue.key) {
      case 'RBF': {
        updateKernelData(() => 'FlatSpline')
        break
      }
      case 'kriging': {
        updateKrigingData(() => krigingDefaultField)
        break
      }
      case 'sequentialConvergence': {
        updateSequentialConvergenceData(() => sequentialConvergenceDefaultField)
        break
      }
      case 'minimumCurvature': {
        updateMinimalCurvatureData(() => minimalCurvatureDefaultField)
        break
      }
      case 'movingAverage': {
        updateMovingAverageData(() => movingAverageDefaultField)
        break
      }
    }
  }, [methodValue])

  const surfaceCalcClassName = cn(css.mapBuildingBody, {
    [css.bodyDisabled]: isCalculating,
  })

  const calculateButtonClassName = cn({
    [css.bodyDisabled]: isCalculating,
  })

  const inputDataTitle =
    addedSelectItemList.length > 1
      ? String(addedSelectItemList.length)
      : addedSelectItemList[0].value?.entityName

  const additionalTextes: Partial<Record<MapBuilderItemType, string>> = {
    object: inputDataTitle,
    geometry: choiceValue.name,
    algorithms: methodValue.label,
    map: formData.buildInfo.name,
  }

  const mpItems = getFullNavItems(additionalTextes)

  const onFaultsFolderUpdate = (value: Nullable<BaseEntity>) => {
    setFaultsFolderValue(value)
  }

  const onSave = () => {
    switch (methodValue.key) {
      case 'RBF': {
        dispatch(calculateSurfaceByRBFAction(formData))
        break
      }
      case 'kriging': {
        dispatch(calculateSurfaceByKrigingAction(formData))
        break
      }
      case 'sequentialConvergence': {
        dispatch(calculateSurfaceBySCAction(formData))
        break
      }
      case 'minimumCurvature': {
        dispatch(calculateSurfaceByMCAction(formData))
        break
      }
      case 'movingAverage': {
        dispatch(calculateSurfaceByMAAction(formData))
        break
      }
    }
  }

  const handleCloseModal = () => {
    closeModal()
    onClose?.()
  }

  const isSelectedAnyHorizons =
    !!formData.buildInfo.sources.interpretations2d.length ||
    !!formData.buildInfo.sources.interpretations3d.length ||
    !!formData.buildInfo.sources.horizons.length

  const canConfirm =
    !isStringNullOrEmpty(formData.buildInfo.name) &&
    isSelectedAnyHorizons &&
    !isCalculating &&
    isValidGeometry(formData.buildInfo.geometry) &&
    isValidMethodParams(formData)

  return (
    <>
      <ModalHeader
        ref={ref}
        title="Построение карт"
        icon={IconGeoMap}
        onCloseClick={handleCloseModal}
      />
      <ModalBody className={surfaceCalcClassName}>
        <ModalNav items={mpItems} groups={mpGroups} onChoose={handleOnMenuChange} />
        <ModalRight className={css.mapBuildingRight}>
          <ModalBuildingRightMenu
            activeStep={activeStepNew}
            setMethodValue={setMethodValue}
            methodValue={methodValue}
            setChoiceValue={setChoiceValue}
            choiceValue={choiceValue}
            faultsFolderValue={faultsFolderValue}
            formData={formData}
            updateSourcesData={updateSourcesData}
            updateGeometryData={updateGeometryData}
            updateKernelData={updateKernelData}
            updateTargetData={updateTargetData}
            updateKrigingData={updateKrigingData}
            updateFaultsData={updateFaultsData}
            addedSelectItemList={addedSelectItemList}
            updateSelectItemList={setAddedSelectItemList}
            updateSequentialConvergenceData={updateSequentialConvergenceData}
            updateMinimalCurvatureData={updateMinimalCurvatureData}
            updateMovingAverageData={updateMovingAverageData}
            onFaultsFolderUpdate={onFaultsFolderUpdate}
          />
        </ModalRight>
      </ModalBody>
      <ModalFooter className={css.mapBuildingFooter}>
        <Footer
          className={calculateButtonClassName}
          canConfirm={canConfirm}
          isLoading={isCalculating}
          onSave={onSave}
        />
      </ModalFooter>
    </>
  )
})

export const MapBuildingModal = usingMovingModal(MapBuilding)
